import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  getAppraisalData,
  getQuestionsAndAnswers
} from "../../../store/selectors";
import { useDispatch } from "react-redux";
import { appraisalSlice } from "../../../store/slices/Appraisal";
import {
  RadioAndTooltipContent,
  defaultAnswerState,
  questionPresentationContent
} from "../../../data/QuestionPresentation";
import { sortAnswers } from "../../helpers";
import { getAnswerFromQuestionById } from "../../../helpers/appraisals";
import { replacePlaceholderValues } from "../../../helpers/replacePlaceholderValues";
import { TooltipReplaceableValues } from "../../../data/QuestionData";

interface useRadioAndToolTipQuestionCompactProps {
  question: Question;
  replaceManufacturerName?: boolean;
}

function useRadioAndToolTipQuestionCompact({
  question,
  replaceManufacturerName
}: useRadioAndToolTipQuestionCompactProps) {
  const dispatch = useDispatch();
  const questionsAndAnswers = useSelector(getQuestionsAndAnswers);
  const storedAnswer = questionsAndAnswers && questionsAndAnswers[question.id];
  const [selectedOption, setSelectedOption] = useState(storedAnswer || []);
  const storedAnswerTooltip =
    getAnswerFromQuestionById(question, storedAnswer)?.tooltip || "";
  const [answerTooltip, setAnswerTooltip] = useState(storedAnswerTooltip);
  const {
    vehicle: { manufacturer }
  } = useSelector(getAppraisalData);

  const sortedAnswers = sortAnswers(question.answers);

  const {
    groupName,
    component: { content }
  } = questionPresentationContent[question.id];

  const compactContent = content as RadioAndTooltipContent;

  const [answer, setAnswer] = useState(defaultAnswerState);

  const toolTipToDisplay = replaceManufacturerName
    ? replacePlaceholderValues(
        answerTooltip,
        TooltipReplaceableValues.MANUFACTURER,
        manufacturer
      )
    : answerTooltip;

  useEffect(() => {
    setAnswer(storedAnswer ? storedAnswer : null);
  }, [question.selectedAnswer, questionsAndAnswers, question.id, storedAnswer]);

  const handleAnswerChange = (answerId: number) => {
    dispatch(appraisalSlice.actions.setAnswersChanged(true));
    dispatch(
      appraisalSlice.actions.updateConditionalAnswersSubmitted(question.id)
    );
    setAnswer([answerId]);
    dispatch(
      appraisalSlice.actions.setQuestionsAndAnswers({
        [question.id]: [answerId]
      })
    );
  };

  return {
    answer,
    answerTooltip: toolTipToDisplay,
    groupName: groupName || "",
    handleChange: handleAnswerChange,
    questionString: question.content,
    selectedOption,
    setAnswerTooltip,
    setSelectedOption,
    sortedAnswers,
    tooltipWidth: compactContent?.compactView?.tooltipWidth
  };
}

export default useRadioAndToolTipQuestionCompact;
